import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '../contexts/ThemeContext';
import { faRobot } from '@/icons';

interface SpinnerOverlayProps {
    isVisible: boolean;
    message: string;
    /**
     * When true, shows animated robot icons marching around the Calculist spinner. Use for long-running AI-backed
     * requests (e.g. Allocation Analysis PDF); pass false for ordinary spinners.
     */
    aiAssisted?: boolean;
}

const AI_ROBOT_COUNT = 5;
/** Half the square side (px): robot path centers sit this far from the spinner center on each axis. */
const AI_SQUARE_TRACK_HALF = 44;
/** One full lap around the square (seconds). */
const AI_SQUARE_DURATION_S = 4.5;

export const SpinnerOverlay: React.FC<SpinnerOverlayProps> = ({ isVisible, message, aiAssisted = false }) => {
    const theme = useTheme();

    if (!isVisible) return null;

    const arenaSize = Math.round(AI_SQUARE_TRACK_HALF * 2 + 48 + 8);

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20000, // Higher than modals (which use 10000) to ensure it overlays everything
            flexDirection: 'column' as const,
            gap: theme.spacing.md
        },
        container: {
            backgroundColor: 'white',
            borderRadius: theme.borderRadius.lg,
            padding: `${theme.spacing.xl} ${theme.spacing.xxl}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            gap: theme.spacing.md,
            minWidth: '200px',
            textAlign: 'center' as const
        },
        spinner: {
            width: '48px',
            height: '48px'
        },
        aiArena: {
            position: 'relative' as const,
            width: `${arenaSize}px`,
            height: `${arenaSize}px`,
            flexShrink: 0,
        },
        spinnerCentered: {
            position: 'absolute' as const,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            width: '48px',
            height: '48px',
        },
        robotOnTrack: {
            position: 'absolute' as const,
            left: '50%',
            top: '50%',
            zIndex: 1,
            pointerEvents: 'none' as const,
        },
        message: {
            color: theme.colors.text.primary,
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.medium
        }
    };

    const robotColor = theme.colors.text.muted;
    const robotSize = theme.typography.fontSize.lg;
    const h = AI_SQUARE_TRACK_HALF;

    return (
        <div style={styles.overlay}>
            {aiAssisted && (
                <style>
                    {`
                        @keyframes spinnerOverlayAiSquare {
                            0% { transform: translate(-50%, -50%) translate(-${h}px, -${h}px); }
                            25% { transform: translate(-50%, -50%) translate(${h}px, -${h}px); }
                            50% { transform: translate(-50%, -50%) translate(${h}px, ${h}px); }
                            75% { transform: translate(-50%, -50%) translate(-${h}px, ${h}px); }
                            100% { transform: translate(-50%, -50%) translate(-${h}px, -${h}px); }
                        }
                    `}
                </style>
            )}
            <div style={styles.container}>
                {aiAssisted ? (
                    <div style={styles.aiArena} aria-hidden>
                        {Array.from({ length: AI_ROBOT_COUNT }, (_, i) => (
                            <span
                                key={i}
                                style={{
                                    ...styles.robotOnTrack,
                                    color: robotColor,
                                    animation: `spinnerOverlayAiSquare ${AI_SQUARE_DURATION_S}s linear infinite`,
                                    animationDelay: `${-((AI_SQUARE_DURATION_S / AI_ROBOT_COUNT) * i)}s`,
                                }}
                            >
                                <FontAwesomeIcon icon={faRobot} style={{ fontSize: robotSize }} />
                            </span>
                        ))}
                        <img
                            src="/logo-spinner.gif"
                            alt="Loading..."
                            style={{ ...styles.spinner, ...styles.spinnerCentered }}
                        />
                    </div>
                ) : (
                    <img
                        src="/logo-spinner.gif"
                        alt="Loading..."
                        style={styles.spinner}
                    />
                )}
                <div style={styles.message}>
                    {message}
                </div>
            </div>
        </div>
    );
};
